import React from "react";
import { Box, Card, Divider, Skeleton, Stack } from "@mui/material";

/**
 * Skeleton loader component for timesheet module.
 * Provides content-aware loading placeholders matching application design & MUI v9 layout.
 *
 * @param {"dashboard" | "jobList" | "selectJob" | "selectEmployee" | "detail" | "modal" | "template" | "templateList"} variant - The skeleton layout variant
 * @param {number} count - Number of skeleton items to render (default: 3)
 */
export function TimesheetSkeletonLoader({ variant = "modal", count = 3 }) {
  switch (variant) {
    case "dashboard":
      return <DashboardSkeleton count={count} />;
    case "jobList":
      return <JobListSkeleton count={count} />;
    case "selectJob":
      return <SelectJobSkeleton count={count} />;
    case "selectEmployee":
      return <SelectEmployeeSkeleton count={count} />;
    case "detail":
      return <DetailSkeleton />;
    case "modal":
      return <ModalSkeleton />;
    case "template":
      return <TemplateSkeleton />;
    case "templateList":
      return <TemplateListSkeleton count={count} />;
    default:
      return <ModalSkeleton />;
  }
}

// Dashboard card list skeleton (for main timesheet listing)
const DashboardSkeleton = ({ count = 3 }) => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box" }}>
    {Array.from({ length: count }).map((_, index) => (
      <Card
        key={index}
        sx={{
          padding: "10px",
          borderRadius: "10px",
          boxShadow: "none",
          border: "1px solid #e4e4e4",
          backgroundColor: "#ffffff",
          width: "100%",
          boxSizing: "border-box"
        }}>
        <Stack spacing={1} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Skeleton variant="text" width={110} height={20} />
              <Skeleton variant="text" width={120} height={16} />
            </Stack>
            <Skeleton variant="circular" width={24} height={24} />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mt: 0.5, width: "100%", boxSizing: "border-box" }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }} divider={<Divider orientation="vertical" flexItem />}>
              <Skeleton variant="text" width={75} height={16} />
              <Skeleton variant="text" width={55} height={16} />
            </Stack>
            <Skeleton variant="rounded" width={75} height={24} sx={{ borderRadius: "16px" }} />
          </Stack>
        </Stack>
      </Card>
    ))}
  </Stack>
);

// Job list skeleton (for clock-in/manual job rows)
const JobListSkeleton = ({ count = 3 }) => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box" }}>
    {Array.from({ length: count }).map((_, index) => (
      <Card
        key={index}
        sx={{
          padding: "10px",
          borderRadius: "10px",
          boxShadow: "none",
          border: "1px solid #e4e4e4",
          backgroundColor: "#ffffff",
          width: "100%",
          boxSizing: "border-box"
        }}>
        <Stack spacing={1} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
            <Stack spacing={0.5} sx={{ width: "70%" }}>
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="60%" height={14} />
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Skeleton variant="circular" width={22} height={22} />
              <Skeleton variant="circular" width={22} height={22} />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, width: "100%", boxSizing: "border-box" }}>
            <Skeleton variant="rounded" width={85} height={30} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" width={85} height={30} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" width={55} height={30} sx={{ borderRadius: "8px" }} />
          </Stack>
        </Stack>
      </Card>
    ))}
  </Stack>
);

// Select Job page/modal skeleton
const SelectJobSkeleton = ({ count = 3 }) => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box", pt: 0.5 }}>
    {/* Header with Title and Sort By */}
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width={120} height={24} />
      <Skeleton variant="text" width={75} height={18} />
    </Stack>

    {/* Job cards list */}
    <Stack spacing={1} sx={{ mt: 0.5, width: "100%", boxSizing: "border-box" }}>
      {Array.from({ length: count }).map((_, index) => (
        <Card
          key={index}
          sx={{
            padding: "10px 12px",
            borderRadius: "10px",
            boxShadow: "none",
            border: "1px solid #e4e4e4",
            backgroundColor: "#ffffff",
            width: "100%",
            boxSizing: "border-box"
          }}>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="circular" width={18} height={18} />
          </Stack>
        </Card>
      ))}
    </Stack>

    {/* Bottom Button */}
    <Skeleton variant="rounded" width="100%" height={38} sx={{ borderRadius: "10px", mt: 0.5 }} />
  </Stack>
);

// Select Employee page/modal skeleton
const SelectEmployeeSkeleton = ({ count = 3 }) => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box", pt: 0.5 }}>
    {/* Header navigation & title */}
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="circular" width={22} height={22} />
      <Skeleton variant="circular" width={22} height={22} />
    </Stack>
    <Skeleton variant="text" width={140} height={24} />

    {/* Search bar input */}
    <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "10px" }} />

    {/* Employee list items (Checkbox + Avatar + Employee Name) */}
    <Stack spacing={1} sx={{ mt: 0.5, width: "100%", boxSizing: "border-box" }}>
      {Array.from({ length: count }).map((_, index) => (
        <Card
          key={index}
          sx={{
            padding: "8px 10px",
            borderRadius: "10px",
            boxShadow: "none",
            border: "1px solid #e4e4e4",
            backgroundColor: "#ffffff",
            width: "100%",
            boxSizing: "border-box"
          }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", width: "100%", boxSizing: "border-box" }}>
            <Skeleton variant="rounded" width={18} height={18} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="text" width="55%" height={18} />
          </Stack>
        </Card>
      ))}
    </Stack>

    {/* Action button */}
    <Skeleton variant="rounded" width="100%" height={38} sx={{ borderRadius: "10px", mt: 0.5 }} />
  </Stack>
);

// Timesheet detail view skeleton
const DetailSkeleton = () => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box", pt: 0.5 }}>
    {/* Header */}
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: "16px" }} />
      <Skeleton variant="text" width={140} height={16} />
    </Stack>

    {/* Date & Job */}
    <Stack spacing={0.5} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width="40%" height={18} />
      <Skeleton variant="text" width="70%" height={20} />
    </Stack>

    {/* Activities Section */}
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
      {[1, 2].map((i) => (
        <Card
          key={i}
          sx={{
            padding: "10px",
            borderRadius: "10px",
            mb: 1,
            boxShadow: "none",
            border: "1px solid #e4e4e4",
            width: "100%",
            boxSizing: "border-box"
          }}>
          <Skeleton variant="text" width="55%" height={18} />
          <Stack direction="row" sx={{ justifyContent: "space-between", mt: 1, width: "100%", boxSizing: "border-box" }}>
            <Skeleton variant="text" width={85} height={16} />
            <Skeleton variant="text" width={40} height={16} />
          </Stack>
        </Card>
      ))}
    </Box>
  </Stack>
);

// Generic modal content skeleton
const ModalSkeleton = () => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box" }}>
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width="45%" height={24} />
      <Skeleton variant="text" width="25%" height={18} />
    </Stack>
    <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "10px" }} />
    <Stack spacing={1} sx={{ mt: 1, width: "100%", boxSizing: "border-box" }}>
      {[1, 2, 3].map((i) => (
        <Card
          key={i}
          sx={{
            padding: "8px 10px",
            border: "1px solid #e4e4e4",
            borderRadius: "8px",
            boxShadow: "none",
            width: "100%",
            boxSizing: "border-box"
          }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", boxSizing: "border-box" }}>
            <Skeleton variant="rounded" width={18} height={18} sx={{ borderRadius: "4px" }} />
            <Skeleton variant="text" width="70%" height={18} />
          </Stack>
        </Card>
      ))}
    </Stack>
    <Skeleton variant="rounded" width="100%" height={38} sx={{ borderRadius: "10px", mt: 1 }} />
  </Stack>
);

// Crew Template Form & View skeleton
const TemplateSkeleton = () => (
  <Stack spacing={1.5} sx={{ width: "100%", boxSizing: "border-box" }}>
    {/* Template Name */}
    <Stack spacing={0.5} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width="35%" height={18} />
      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "10px" }} />
    </Stack>

    {/* Job Selection */}
    <Stack spacing={0.5} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width="25%" height={18} />
      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "10px" }} />
    </Stack>

    {/* Activities Selection */}
    <Stack spacing={0.5} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Skeleton variant="text" width="30%" height={18} />
      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: "10px" }} />
    </Stack>

    {/* Action Button */}
    <Skeleton variant="rounded" width="100%" height={38} sx={{ borderRadius: "10px", mt: 1 }} />
  </Stack>
);

// Horizontal Template Card List skeleton (for AddTimesheetModal template section)
const TemplateListSkeleton = ({ count = 3 }) => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{ mt: 1, width: "100%", boxSizing: "border-box", overflowX: "auto", scrollbarWidth: "none" }}>
    {Array.from({ length: count }).map((_, index) => (
      <Card
        key={index}
        sx={{
          padding: "10px",
          borderRadius: "10px",
          boxShadow: "none",
          border: "1px solid #e4e4e4",
          minWidth: "150px",
          flexShrink: 0,
          boxSizing: "border-box"
        }}>
        <Stack spacing={0.8} sx={{ boxSizing: "border-box" }}>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Skeleton variant="text" width="65%" height={18} />
            <Skeleton variant="circular" width={16} height={16} />
          </Stack>
          <Skeleton variant="text" width="85%" height={14} />
          <Skeleton variant="text" width="50%" height={12} />
        </Stack>
      </Card>
    ))}
  </Stack>
);

export default TimesheetSkeletonLoader;
